import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { relationOf } from './mechanics/relations';
import './SceneArranger.css';

/**
 * The creator places every item in the scene themselves (backend game.save_layout). Click an item
 * to pick it up, click anywhere on the background to put it there (its feet land where you click),
 * or drag it. The size slider sets its width. Saved positions are used by every later build/play.
 * Positions are % of the scene: x/y = the item's bottom centre, width = % of the scene width.
 *
 * Problem pairs (spec.pairs, backend game.problem_pairs) only show the problem when seen together -
 * boxes in a camera's view, a pallet propping a door. Only LOCKED pairs are kept together: moving the
 * host carries its parts, and a part dropped away snaps back (same rule as backend game.enforce_pairs).
 * A placement ("the cup starts on the counter by the bottle") is just where it starts - move it freely.
 *
 * Pictures: each item's picture was matched by the art check (spec.art); pick another for the selected
 * item from the sheet's pictures (spec.pictures) - any picture, even one another item uses (two cups can
 * share the cup) - your choice wins on every later build. Or Redraw it: one image call for that item alone,
 * in your own words ("a clear plastic measuring cup with ml marks").
 * The checklist (spec.checklist, backend game.checklist) sits on top: what to put right before publishing.
 *
 * Library (inventory missions): the background comes from the shared library of rooms - "Background…" shows the room's
 * other backgrounds (free to pick) before "Generate 4 new ones…" (one image call: four versions of the room, cut apart
 * and added to the library for everyone - in the creator's own words if given; the window stays open to pick one).
 * "Draw pictures" takes words for each ticked item, a redraw and the new-views sheet words for theirs. A
 * selected inventory item shows its pictures there too (its own, and any a creator had drawn).
 *
 * Views (backend game.views_info / save_views): more sections of the work area besides the main room - up to four in
 * all. A tab per view; "+ Add a view" takes any section of this setting from the library (free) or draws new ones (up to
 * four in ONE image call). Each object stands in one view (its "View" choice); a view can be renamed, given another
 * background or removed (its objects go back to the main room). "⇋ Flip" mirrors an object; "Mirror this view" swaps
 * the whole view left to right. Saved with the layout.
 */
const MAIN = 'main';
// the draw boxes start with the plan's own places the library hasn't drawn yet (backend views_info `hints`)
const fourPlaces = (hints) => [...(hints || []), '', '', '', ''].slice(0, 4);
const slugOf = (text) => (String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'view');
const titleOf = (text) => String(text).charAt(0).toUpperCase() + String(text).slice(1);
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const LEVEL_ICON = { ok: '✅', warn: '⚠️', error: '⛔' };

const ART_NOTE = {
  creator: 'your pick', redrawn: 'redrawn for it', checked: 'matched by the art check',
  shared: "borrowed from its look-alike", order: 'not checked - have a look', missing: 'no picture shows it yet'
};

// `initialSelected`: the item (its name) to open on - one clicked in the mission card's preview
// how an area of the floor can be marked in the game (backend game.AREA_LOOKS) - hidden: not marked, the player can show it
export const AREA_LOOKS = [['hidden', 'Hidden (player can show it)'], ['outline', 'Outline'], ['tape', 'Floor tape'],
  ['stripes', 'Hazard stripes'], ['dashed', 'Dashed line']];
const isArea = (o) => o.kind === 'place' && !!o.inv?.zone;

export default function SceneArranger({ spec, onSave, onCancel, onRedoBackground = null, onRedraw = null, library = null, initialSelected = null }) {
  const stageRef = useRef(null);
  const itemRefs = useRef({});
  const dragRef = useRef(null);
  const [positions, setPositions] = useState(null);   // { item: {x, y, width} }
  const [selected, setSelected] = useState(initialSelected);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The creator's picture choices: {item id: picture number}; starts from the ones they made before.
  const [art, setArt] = useState(() => Object.fromEntries(Object.entries(spec.art || {}).filter(([, a]) => a.by === 'creator').map(([id, a]) => [id, a.picture])));
  const pictureOf = (o) => (art[o.id] !== undefined ? spec.pictures?.[art[o.id]]?.src : o.src);
  const [redraw, setRedraw] = useState(null);         // { id, looks, busy, error } - the redraw form for one item
  const [rooms, setRooms] = useState(null);           // the room's backgrounds in the library, while the picker is open
  const [itemPics, setItemPics] = useState(null);     // { id, current, variants } for the selected inventory item
  const [toDraw, setToDraw] = useState([]);           // item ids ticked for "Draw pictures"
  const [drawing, setDrawing] = useState(false);
  const [drawError, setDrawError] = useState('');      // shown by the button (the top of the window may be out of view)
  const [drawLooks, setDrawLooks] = useState({});      // {item id: the creator's words for how its picture should look}
  const [newBackground, setNewBackground] = useState(null);   // { looks, busy, error } - the "new backgrounds" form, while open
  const drawPictures = async () => {
    setDrawing(true);
    setDrawError('');
    setError('');
    const looks = Object.fromEntries(toDraw.map((id) => [id, (drawLooks[id] || '').trim()]).filter(([, w]) => w));
    try { await library.drawMany(toDraw, looks); setToDraw([]); setDrawLooks({}); } catch (e) { setDrawError(e.message); } finally { setDrawing(false); }
  };
  const generateBackgrounds = async () => {
    setNewBackground((b) => ({ ...b, busy: true, error: '' }));
    try {
      await onRedoBackground(newBackground.looks.trim());
      setNewBackground(null);
      if (library) setRooms(await library.backgrounds());            // the four new ones join the picker
    } catch (e) { setNewBackground((b) => ({ ...b, busy: false, error: e.message })); }
  };
  const drawable = spec.items.filter((o) => o.inv && o.kind !== 'place');
  // a drawn sheet's picture on the wrong item: this one takes the other's picture and gives its own
  const [swapping, setSwapping] = useState(false);
  const swapWith = async (o, other) => {
    if (!other) return;
    setSwapping(true);
    setDrawError('');
    try {
      await library.swapPicture(o.id, other);
      setArt((a) => { const next = { ...a }; delete next[o.id]; delete next[other]; return next; });   // show the swapped pictures
    } catch (e) { setDrawError(e.message); } finally { setSwapping(false); }
  };
  const openBackgrounds = async () => {
    try { setRooms(await library.backgrounds()); } catch (e) { setError(e.message); }
  };
  const pickBackground = async (variant) => {
    try { await library.chooseBackground(variant); setRooms((r) => ({ ...r, current: variant })); } catch (e) { setError(e.message); }
  };
  const startRedraw = (o) => setRedraw({ id: o.id, looks: o.looks || '', busy: false, error: '' });
  const doRedraw = async () => {
    setRedraw((r) => ({ ...r, busy: true, error: '' }));
    try {
      await onRedraw(redraw.id, redraw.looks.trim());
      setArt((a) => { const next = { ...a }; delete next[redraw.id]; return next; });   // show the new drawing
      setRedraw(null);
    } catch (e) {
      setRedraw((r) => ({ ...r, busy: false, error: e.message }));
    }
  };

  // ---- views: the main room and the other sections of the work area (none: as before, one scene)
  const [sections, setSections] = useState(null);      // { views: [{id, name, room, background, src}], rooms, setting }
  const [view, setView] = useState(MAIN);
  const [viewOf, setViewOf] = useState({});            // object id -> view id (none: the main room)
  const [flipped, setFlipped] = useState([]);          // object ids drawn mirrored
  // how each area of the floor is marked in the game (backend game.AREA_LOOKS) - hidden unless the creator picks a marking
  const [areaLooks, setAreaLooks] = useState(() => Object.fromEntries(spec.items.filter(isArea).map((o) => [o.id, o.area_look || 'hidden'])));
  const [viewsDirty, setViewsDirty] = useState(false);
  const [adding, setAdding] = useState(false);          // the "add a view" panel
  const [places, setPlaces] = useState(['', '', '', '']);
  const [viewsLooks, setViewsLooks] = useState('');    // the creator's words for how the new sections should look
  const [drawingViews, setDrawingViews] = useState(false);
  const [drawnRooms, setDrawnRooms] = useState([]);    // the sections just drawn - marked "new" in the panel
  const [movedNote, setMovedNote] = useState('');
  const srcOf = (rooms, room, background) => rooms.find((r) => r.room === room)?.variants.find((v) => v.id === background)?.src;
  useEffect(() => {
    let live = true;
    if (library?.views) {
      library.views().then((info) => {
        if (!live) return;
        setSections({ ...info, views: info.views.map((v) => ({ ...v, src: v.id === MAIN ? spec.background_src : srcOf(info.rooms, v.room, v.background) })) });
        setViewOf(info.view_of || {});
        setFlipped(info.flipped || []);
        setPlaces(fourPlaces(info.hints));
      }).catch(() => {});
    }
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const byIdAll = Object.fromEntries(spec.items.map((o) => [o.id, o]));
  // the view the creator put a thing in - else, on or in something, where its holder stands
  const holderOfItem = (o) => {
    const holder = byIdAll[String(o.state?.where || '').split(':')[0]];
    return holder && holder.id !== o.id ? holder : null;
  };
  const viewOfItem = (o, depth = 0) => {
    if (viewOf[o.id]) return viewOf[o.id];
    const holder = holderOfItem(o);
    return holder && depth < 4 ? viewOfItem(holder, depth + 1) : MAIN;
  };
  const [askRiders, setAskRiders] = useState(null);    // { item, view, riders }: what's on a thing being moved - with it?
  const shown = (o) => !sections || viewOfItem(o) === view;
  const current = sections?.views.find((v) => v.id === view) || null;
  const changeViews = (fn) => { setSections((sec) => ({ ...sec, views: fn(sec.views) })); setViewsDirty(true); };
  const addView = (room, variant) => {
    const taken = new Set(sections.views.map((v) => v.id));
    let id = slugOf(room.place);
    for (let n = 2; taken.has(id) || id === MAIN; n += 1) id = `${slugOf(room.place)}-${n}`;
    changeViews((vs) => [...vs, { id, name: titleOf(room.place), room: room.room, background: variant.id, src: variant.src }]);
    setView(id);
    setAdding(false);
  };
  const removeView = (id) => {
    changeViews((vs) => vs.filter((v) => v.id !== id));
    setViewOf((m) => Object.fromEntries(Object.entries(m).filter(([, v]) => v !== id)));      // its objects: back in the main room
    setView(MAIN);
  };
  // Moving a thing moves only that thing. What stands on or in it (the bottles on the bench) - the creator chooses:
  // with it, or left where they are.
  const ridersOf = (o) => spec.items.filter((x) => x.id !== o.id && !viewOf[x.id] && !x.inv?.made_by && (() => {
    for (let h = holderOfItem(x), n = 0; h && n < 4; h = holderOfItem(h), n += 1) if (h.id === o.id) return true;
    return false;
  })());
  const nameOfView = (id) => sections.views.find((v) => v.id === id)?.name;
  const moveToView = (o, id) => {
    const riders = ridersOf(o);
    if (riders.length) { setAskRiders({ item: o, view: id, riders }); return; }
    placeIn(o, id, [], false);
  };
  const placeIn = (o, id, riders, together) => {
    const from = viewOfItem(o);
    const on = holderOfItem(o);
    setViewOf((m) => {
      const next = { ...m, [o.id]: id };
      if (on && viewOfItem(on) === id) delete next[o.id];                      // back with what it stands on: it follows it
      if (!together) riders.forEach((x) => { next[x.id] = from; });          // left where they are
      return next;
    });
    setViewsDirty(true);
    setAskRiders(null);
    setMovedNote(together ? `${o.item} and what's on it (${riders.map((x) => x.item).join(', ')}) are now in ${nameOfView(id)}.`
      : riders.length ? `${o.item} is now in ${nameOfView(id)} - ${riders.map((x) => x.item).join(', ')} stayed here.`
        : on ? `${o.item} is now in ${nameOfView(id)} on its own (it started on ${on.item}).` : `${o.item} is now in ${nameOfView(id)}.`);
    setSelected(null);
  };
  const inUse = (room, background) => sections.views.some((v) => v.room === room && v.background === background);
  const toggleFlip = (id) => { setFlipped((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id])); setViewsDirty(true); };
  // the whole view, left to right: every object here moves to the other side and faces the other way
  const mirrorView = () => {
    const here = spec.items.filter((o) => shown(o) && !o.inv?.made_by);
    setPositions((p) => {
      const next = { ...p };
      here.forEach((o) => { if (next[o.item]) next[o.item] = { ...next[o.item], x: clamp(100 - next[o.item].x, 0, 100) }; });
      return next;
    });
    const things = here.filter((o) => o.kind !== 'place').map((o) => o.id);
    setFlipped((f) => [...f.filter((x) => !things.includes(x)), ...things.filter((x) => !f.includes(x))]);
    setViewsDirty(true);
  };
  const drawSections = async () => {
    setDrawingViews(true);
    setError('');
    try {
      const out = await library.drawViews(places.map((x) => x.trim()).filter(Boolean), viewsLooks.trim());
      setSections((sec) => ({ ...sec, rooms: out.rooms }));
      setDrawnRooms(out.drawn.map((d) => d.room));
      setPlaces(['', '', '', '']);
      setViewsLooks('');
    } catch (e) { setError(e.message); } finally { setDrawingViews(false); }
  };

  const selectedItem = spec.items.find((o) => o.item === selected);
  useEffect(() => {
    let live = true;
    setItemPics(null);
    if (library && selectedItem?.inv) {
      library.pictures(selectedItem.id).then((p) => { if (live) setItemPics({ id: selectedItem.id, ...p }); }).catch(() => {});
    }
    return () => { live = false; };
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickPicture = async (ref) => {
    try { await library.choosePicture(selectedItem.id, ref); setItemPics((p) => ({ ...p, current: ref })); } catch (e) { setError(e.message); }
  };
  // an object drawn per state (frayed / replaced): a picture is chosen for each state
  const pickStatePicture = async (state, ref) => {
    try {
      await library.choosePicture(selectedItem.id, ref, state);
      setItemPics((p) => ({ ...p, states: p.states.map((x) => (x.state === state ? { ...x, current: ref } : x)) }));
    } catch (e) { setError(e.message); }
  };

  // Start from where the items currently stand: measure them as rendered.
  useLayoutEffect(() => {
    if (!stageRef.current) return;
    // (each view's objects are measured the first time it's shown)
    const missing = spec.items.filter((o) => itemRefs.current[o.item] && !(positions && positions[o.item]));
    if (positions && !missing.length) return;
    const stage = stageRef.current.getBoundingClientRect();
    const start = {};
    missing.forEach((o) => {
      const el = itemRefs.current[o.item];
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!stage.width || !stage.height) {                // not laid out yet (hidden): where its own style puts it
        const w = parseFloat(o.style?.width) || 10;
        start[o.item] = { x: clamp((parseFloat(o.style?.left) || 0) + w / 2, 0, 100), y: clamp(100 - (parseFloat(o.style?.bottom) || 0), 0, 100),
          width: clamp(w, 2, 60) };
        return;
      }
      start[o.item] = {
        x: clamp(((r.left + r.width / 2 - stage.left) / stage.width) * 100, 0, 100),
        y: clamp(((r.bottom - stage.top) / stage.height) * 100, 0, 100),
        width: clamp((r.width / stage.width) * 100, 2, 60)
      };
    });
    setPositions((p) => ({ ...(p || {}), ...start }));
  }, [positions, spec.items, view, sections]);

  const toScene = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100), y: clamp(((e.clientY - r.top) / r.height) * 100, 0, 100) };
  };
  const nameOf = Object.fromEntries(spec.items.map((o) => [o.id, o.item]));
  const byName = Object.fromEntries(spec.items.map((o) => [o.item, o]));
  const pairs = (spec.pairs || []).filter((pr) => pr.locked !== false && nameOf[pr.host] && nameOf[pr.part])
    .map((pr) => ({ ...pr, host: nameOf[pr.host], part: nameOf[pr.part] }));

  // Height of an item in % of the scene, measured as rendered (the image decides it).
  const heightOf = (item) => {
    const el = itemRefs.current[item];
    const stage = stageRef.current?.getBoundingClientRect();
    return el && stage ? (el.getBoundingClientRect().height / stage.height) * 100 : 10;
  };
  // Put a part back on its host when it has drifted off (backend enforce_pairs uses the same rule).
  const snapPart = (p, pair) => {
    const h = p[pair.host];
    const q = p[pair.part];
    if (!h || !q) return p;
    const hh = heightOf(pair.host);
    const qh = heightOf(pair.part);
    const { ok, spot } = relationOf(pair).fits({ x: h.x, y: h.y, w: h.width, h: hh }, { x: q.x, y: q.y, w: q.width, h: qh });
    return ok ? p : { ...p, [pair.part]: { ...q, x: clamp(spot.x, 0, 100), y: clamp(spot.y, 0, 99) } };
  };
  const keepPairs = (p) => pairs.reduce(snapPart, p);

  // Moving a host carries its parts by the same amount.
  const moveTo = (item, point) => setPositions((p) => {
    const before = p[item];
    const half = (before.width || 0) / 2;                // the whole sprite stays inside the scene
    const next = { ...p, [item]: { ...before, ...point, x: clamp(point.x, half, 100 - half) } };
    pairs.filter((pr) => pr.host === item && next[pr.part]).forEach((pr) => {
      next[pr.part] = { ...next[pr.part], x: clamp(next[pr.part].x + (point.x - before.x), 0, 100), y: clamp(next[pr.part].y + (point.y - before.y), 0, 100) };
    });
    return next;
  });

  const onItemDown = (e, item) => {
    e.stopPropagation();
    setSelected(item);
    dragRef.current = { item, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onItemMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    d.moved = true;
    moveTo(d.item, toScene(e));
  };
  const onItemUp = () => { dragRef.current = null; setPositions(keepPairs); };
  const onStageClick = (e) => {
    if (!selected) return;
    moveTo(selected, toScene(e));
    setPositions(keepPairs);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (viewsDirty && library?.saveViews) {
        await library.saveViews({ views: sections.views.filter((v) => v.id !== MAIN).map(({ id, name, room, background }) => ({ id, name, room, background })),
          viewOf, flipped });
      }
      await onSave(positions, art, areaLooks);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const styleFor = (o) => {
    const p = positions?.[o.item];
    if (!p) return o.style;
    return { left: `${clamp(p.x - p.width / 2, 0, 100 - p.width)}%`, bottom: `${100 - p.y}%`, width: `${p.width}%`, zIndex: Math.round(p.y / 10) + 1 };
  };

  return createPortal(
    <div className="sa-backdrop" role="dialog" aria-modal="true">
      <div className="sa-window">
        <header className="sa-header">
          <div>
            <h3>Arrange the scene - {spec.mission.name}</h3>
            <span>Click an item, then click where it should stand (or drag it). Its feet land where you click.</span>
          </div>
          <button type="button" className="sa-btn sa-quiet" onClick={onCancel}>Cancel</button>
          <button type="button" className="sa-btn" disabled={!positions || saving} onClick={save}>{saving ? 'Saving…' : 'Save layout'}</button>
        </header>
        {error && <div className="sa-error">{error}</div>}
        {library && (
          <div className="sa-library">
            {!rooms ? <button type="button" className="sa-btn sa-quiet" onClick={openBackgrounds}>Background…</button> : (
              <span className="sa-pictures" role="radiogroup" aria-label="Background">
                <span className="sa-muted">This room's backgrounds (free):</span>
                {rooms.variants.map((v) => (
                  <button key={v.id} type="button" role="radio" aria-checked={rooms.current === v.id} title={`Used ${v.uses} time(s)`}
                    className={`sa-picture sa-bg ${rooms.current === v.id ? 'is-selected' : ''}`} onClick={() => pickBackground(v.id)}>
                    <img src={asset(v.src)} alt="" />
                  </button>
                ))}
                {onRedoBackground && !newBackground && (
                  <button type="button" className="sa-btn sa-quiet" onClick={() => setNewBackground({ looks: '', busy: false, error: '' })}>Generate 4 new ones…</button>
                )}
                {onRedoBackground && newBackground && (
                  <span className="sa-redraw">
                    <input type="text" maxLength={160} value={newBackground.looks} aria-label="How the new backgrounds should look"
                      placeholder="How they should look (optional) - e.g. modern steel shelves, bright evening light" disabled={newBackground.busy}
                      onChange={(e) => setNewBackground((b) => ({ ...b, looks: e.target.value }))} />
                    <button type="button" className="sa-btn" disabled={newBackground.busy} onClick={generateBackgrounds}>
                      {newBackground.busy ? 'Drawing 4 backgrounds…' : 'Generate 4 (1 image call)'}
                    </button>
                    {!newBackground.busy && <button type="button" className="sa-btn sa-quiet" onClick={() => setNewBackground(null)}>Cancel</button>}
                    {newBackground.error && <span className="sa-error-inline" role="alert">{newBackground.error}</span>}
                  </span>
                )}
                <button type="button" className="sa-btn sa-quiet" onClick={() => { setRooms(null); setNewBackground(null); }}>Close</button>
              </span>
            )}
          </div>
        )}
        {(spec.checklist || []).length > 0 && (
          <details className="sa-checklist" open={(spec.checklist || []).some((c) => c.level !== 'ok')}>
            <summary>Before you publish: {(spec.checklist || []).filter((c) => c.level === 'error').length} to fix,
              {' '}{(spec.checklist || []).filter((c) => c.level === 'warn').length} to check</summary>
            <ul>
              {spec.checklist.map((c) => (
                <li key={`${c.id}-${c.text}`} className={`sa-check is-${c.level}`}>
                  <span>{LEVEL_ICON[c.level]}</span> {c.text}
                  {c.action === 'redo_background' && onRedoBackground && (
                    <button type="button" className="sa-btn sa-quiet" onClick={() => Promise.resolve(onRedoBackground()).catch((e) => setError(e.message))}>Redo background</button>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
        {pairs.length > 0 && (
          <div className="sa-pairs">
            <strong>Kept together so the problem shows:</strong>
            {pairs.map((pr) => <span key={`${pr.host}-${pr.part}`} className="sa-pair">{relationOf(pr).text(pr.part, pr.host)} ✓</span>)}
          </div>
        )}

        {sections && (
          <div className="sa-views">
            <span role="tablist" aria-label="Views" className="sa-view-tabs">
              {sections.views.map((v) => (
                <button key={v.id} type="button" role="tab" aria-selected={view === v.id} className={`sa-view-tab ${view === v.id ? 'is-selected' : ''}`}
                  onClick={() => { setView(v.id); setAdding(false); setSelected(null); }}>
                  {v.name} <small>({spec.items.filter((o) => !o.inv?.made_by && viewOfItem(o) === v.id).length})</small>
                </button>
              ))}
              {sections.views.length < 4 && (
                <button type="button" className="sa-btn sa-quiet" aria-expanded={adding} onClick={() => setAdding((a) => !a)}>+ Add a view</button>
              )}
            </span>
            {current && current.id !== MAIN && (
              <span className="sa-view-tools">
                <input type="text" aria-label="View name" maxLength={40} value={current.name}
                  onChange={(e) => changeViews((vs) => vs.map((v) => (v.id === current.id ? { ...v, name: e.target.value } : v)))} />
                <select aria-label="View background" value={`${current.room}|${current.background}`}
                  onChange={(e) => {
                    const [room, background] = e.target.value.split('|');
                    changeViews((vs) => vs.map((v) => (v.id === current.id ? { ...v, room, background, src: srcOf(sections.rooms, room, background) } : v)));
                  }}>
                  {sections.rooms.flatMap((r) => r.variants.map((x, n) => (
                    <option key={`${r.room}|${x.id}`} value={`${r.room}|${x.id}`}>{titleOf(r.place)}{r.variants.length > 1 ? ` (${n + 1})` : ''}</option>
                  )))}
                </select>
                <button type="button" className="sa-btn sa-quiet" onClick={() => removeView(current.id)}>Remove this view</button>
              </span>
            )}
            <button type="button" className="sa-btn sa-quiet" onClick={mirrorView} title="Every object in this view moves to the other side and faces the other way">
              ⇋ Mirror this view
            </button>
            {movedNote && <span className="sa-muted" role="status">{movedNote}</span>}
            {askRiders && (
              <span className="sa-ask" role="alertdialog" aria-label={`What's on ${askRiders.item.item}`}>
                {askRiders.riders.length === 1 ? '1 thing is' : `${askRiders.riders.length} things are`} on {askRiders.item.item}:{' '}
                {askRiders.riders.map((x) => x.item).join(', ')}.
                <button type="button" className="sa-btn" onClick={() => placeIn(askRiders.item, askRiders.view, askRiders.riders, true)}>Move them too</button>
                <button type="button" className="sa-btn sa-quiet" onClick={() => placeIn(askRiders.item, askRiders.view, askRiders.riders, false)}>Leave them here</button>
                <button type="button" className="sa-btn sa-quiet" onClick={() => setAskRiders(null)}>Cancel</button>
              </span>
            )}
          </div>
        )}
        {sections && adding && (
          <div className="sa-add-view">
            <strong>Use a section of this {sections.setting} from the library (free)</strong>
            <div className="sa-rooms">
              {sections.rooms.map((r) => (
                <span key={r.room} className="sa-room">
                  <span className="sa-muted">{titleOf(r.place)}{drawnRooms.includes(r.room) ? ' - new' : ''}</span>
                  {r.variants.map((x) => (inUse(r.room, x.id)
                    ? <span key={x.id} className="sa-picture sa-bg is-in-use" title={`${r.place} - already a view`}><img src={asset(x.src)} alt={r.place} /></span>
                    : (
                      <button key={x.id} type="button" className="sa-picture sa-bg" title={`Add ${r.place} as a view`} onClick={() => addView(r, x)}>
                        <img src={asset(x.src)} alt={r.place} />
                      </button>
                    )))}
                </span>
              ))}
            </div>
            {library.drawViews && (
              <div className="sa-draw-views">
                <strong>Or draw new sections - up to four in one image call</strong>
                {places.map((x, n) => (
                  <input key={n} type="text" maxLength={40} aria-label={`New section ${n + 1}`} placeholder={`Another part of the ${sections.setting}`} value={x}
                    disabled={drawingViews} onChange={(e) => setPlaces((ps) => ps.map((y, k) => (k === n ? e.target.value : y)))} />
                ))}
                <input type="text" maxLength={160} aria-label="How the new sections should look" value={viewsLooks} disabled={drawingViews}
                  placeholder="How they should look (optional) - e.g. tiled floor, cool white lights" onChange={(e) => setViewsLooks(e.target.value)} />
                <button type="button" className="sa-btn" disabled={drawingViews || !places.some((x) => x.trim())} onClick={drawSections}>
                  {drawingViews ? 'Drawing…' : 'Draw them (1 image call)'}
                </button>
              </div>
            )}
          </div>
        )}

        <div ref={stageRef} className={`sa-stage ${selected ? 'is-placing' : ''}`}
          style={{ backgroundImage: `url(${asset(current && current.id !== MAIN ? current.src : spec.background_src)})` }}
          onClick={onStageClick}>
          {/* a thing made during the game (the printed label) isn't in the scene - it appears on its maker or in the bag */}
          {spec.items.filter((o) => !o.inv?.made_by && shown(o)).map((o) => (
            <div key={o.id} ref={(el) => { itemRefs.current[o.item] = el; }}
              className={`sa-item ${selected === o.item ? 'is-selected' : ''} ${o.kind === 'place' ? 'sa-place' : ''} ${flipped.includes(o.id) ? 'sa-flipped' : ''}`}
              style={styleFor(o)} onPointerDown={(e) => onItemDown(e, o.item)} onPointerMove={onItemMove} onPointerUp={onItemUp}
              onClick={(e) => e.stopPropagation()}>
              {isArea(o) ? <span className={`sa-zone gb-preview-zone gb-preview-zone-${o.inv.zone} gb-area-look-${areaLooks[o.id] || 'hidden'}`}>{o.item}</span>
                : o.kind === 'place' ? <span className="sa-ring" /> : pictureOf(o) ? <img src={asset(pictureOf(o))} alt={o.item} draggable={false} />
                : <span className="sa-icon" aria-hidden="true">{o.inv?.icon || '❔'}</span>}
              <span className="sa-label">{o.item}</span>
            </div>
          ))}
        </div>

        <div className="sa-controls">
          {selected && positions?.[selected] ? (
            <>
              <strong>{selected}</strong>
              <label>Size
                <input type="range" min={2} max={60} step={0.5} value={positions[selected].width}
                  onChange={(e) => setPositions((p) => ({ ...p, [selected]: { ...p[selected], width: Number(e.target.value) } }))} />
              </label>
              {byName[selected] && isArea(byName[selected]) && (
                <label>Marking
                  <select aria-label="Marking" value={areaLooks[byName[selected].id] || 'hidden'}
                    onChange={(e) => setAreaLooks((m) => ({ ...m, [byName[selected].id]: e.target.value }))}>
                    {AREA_LOOKS.map(([look, label]) => <option key={look} value={look}>{label}</option>)}
                  </select>
                </label>
              )}
              {sections && byName[selected] && sections.views.length > 1 && (
                <label>View
                  <select aria-label="View" value={viewOfItem(byName[selected])} onChange={(e) => moveToView(byName[selected], e.target.value)}>
                    {sections.views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </label>
              )}
              {library?.saveViews && byName[selected] && byName[selected].kind !== 'place' && (
                <button type="button" className="sa-btn sa-quiet" aria-pressed={flipped.includes(byName[selected].id)}
                  onClick={() => toggleFlip(byName[selected].id)}>⇋ Flip</button>
              )}
              {byName[selected] && byName[selected].kind !== 'place' && (spec.pictures || []).length > 1 && (
                <span className="sa-pictures" role="radiogroup" aria-label="Picture">
                  <span className="sa-muted">Picture</span>
                  {spec.pictures.map((pic) => {
                    const o = byName[selected];
                    const current = pictureOf(o) === pic.src;
                    return (
                      <button key={pic.index} type="button" role="radio" aria-checked={current} title={`Picture ${pic.index + 1}`}
                        className={`sa-picture ${current ? 'is-selected' : ''}`} onClick={() => setArt((a) => ({ ...a, [o.id]: pic.index }))}>
                        <img src={asset(pic.src)} alt="" />
                      </button>
                    );
                  })}
                </span>
              )}
              {itemPics && itemPics.id === byName[selected]?.id && (itemPics.states || []).map((x) => (
                <span key={x.state} className="sa-pictures" role="radiogroup" aria-label={`When ${x.word}`}>
                  <span className="sa-muted">When {x.word}</span>
                  {x.variants.length === 0 && <span className="sa-muted">not drawn yet</span>}
                  {x.variants.map((v) => (
                    <button key={v.ref} type="button" role="radio" aria-checked={x.current === v.ref} title={`${selected} - ${x.word}`}
                      className={`sa-picture ${x.current === v.ref ? 'is-selected' : ''}`} onClick={() => pickStatePicture(x.state, v.ref)}>
                      <img src={asset(v.src)} alt="" />
                    </button>
                  ))}
                </span>
              ))}
              {itemPics && itemPics.id === byName[selected]?.id && !(itemPics.states || []).length && itemPics.variants.length > 1 && (
                <span className="sa-pictures" role="radiogroup" aria-label="Its pictures">
                  <span className="sa-muted">Its pictures</span>
                  {itemPics.variants.map((v) => (
                    <button key={v.ref} type="button" role="radio" aria-checked={itemPics.current === v.ref} title={v.looks || 'its own picture'}
                      className={`sa-picture ${itemPics.current === v.ref ? 'is-selected' : ''}`} onClick={() => pickPicture(v.ref)}>
                      <img src={asset(v.src)} alt="" />
                    </button>
                  ))}
                </span>
              )}
              {byName[selected] && byName[selected].kind !== 'place' && (
                <span className="sa-muted sa-art-note">{art[byName[selected].id] !== undefined ? ART_NOTE.creator
                  : spec.art?.[byName[selected].id]?.by ? ART_NOTE[spec.art[byName[selected].id].by]
                    : pictureOf(byName[selected]) ? '' : ART_NOTE.missing}</span>
              )}
              {byName[selected] && byName[selected].kind !== 'place' && onRedraw && (!redraw || redraw.id !== byName[selected].id) && (
                <button type="button" className="sa-btn sa-quiet" onClick={() => startRedraw(byName[selected])}>Redraw…</button>
              )}
              {redraw && byName[selected]?.id === redraw.id && (
                <span className="sa-redraw">
                  <input type="text" maxLength={160} placeholder={`How ${selected} should look (optional)`} value={redraw.looks}
                    aria-label="How it should look" onChange={(e) => setRedraw((r) => ({ ...r, looks: e.target.value }))} disabled={redraw.busy} />
                  <button type="button" className="sa-btn" disabled={redraw.busy} onClick={doRedraw}>{redraw.busy ? 'Drawing…' : 'Redraw (1 image call)'}</button>
                  {!redraw.busy && <button type="button" className="sa-btn sa-quiet" onClick={() => setRedraw(null)}>Cancel</button>}
                  {redraw.error && <span className="sa-error-inline">{redraw.error}</span>}
                </span>
              )}
              {library?.swapPicture && byName[selected]?.inv && (
                <select className="sa-swap" aria-label="Swap picture with" value="" disabled={swapping}
                  onChange={(e) => swapWith(byName[selected], e.target.value)}>
                  <option value="">{swapping ? 'Swapping…' : 'Swap picture with…'}</option>
                  {drawable.filter((o) => o.id !== byName[selected].id).map((o) => <option key={o.id} value={o.id}>{o.item}</option>)}
                </select>
              )}
              <button type="button" className="sa-btn sa-quiet" onClick={() => setSelected(null)}>Done with this item</button>
            </>
          ) : <span className="sa-muted">Pick an item to place it.</span>}
          <span className="sa-list">
            {spec.items.map((o) => (
              <span key={o.id} className="sa-chip-wrap">
                {library?.drawMany && o.inv && o.kind !== 'place' && (
                  <input type="checkbox" aria-label={`Draw a picture for ${o.item}`} checked={toDraw.includes(o.id)} disabled={drawing}
                    onChange={() => setToDraw((t) => (t.includes(o.id) ? t.filter((x) => x !== o.id) : [...t, o.id]))} />
                )}
                <button type="button" className={`sa-chip ${selected === o.item ? 'is-selected' : ''}`} onClick={() => setSelected(o.item)}>
                  {!o.src && o.inv?.icon ? `${o.inv.icon} ` : ''}{o.item}
                </button>
              </span>
            ))}
          </span>
          {library?.drawMany && drawable.length > 0 && (
            <span className="sa-draw-many">
              <button type="button" className="sa-btn sa-quiet" disabled={drawing} onClick={() => setToDraw(drawable.filter((o) => !o.src).map((o) => o.id))}>
                Tick the ones without a picture
              </button>
              <button type="button" className="sa-btn" disabled={!toDraw.length || drawing} onClick={drawPictures}>
                {drawing ? `Drawing ${toDraw.length}…` : toDraw.length > 1 ? `Draw ${toDraw.length} pictures (1 image call)` : `Draw pictures (${toDraw.length} image call${toDraw.length === 1 ? '' : 's'})`}
              </button>
              {drawError && <span className="sa-error-inline" role="alert">{drawError}</span>}
            </span>
          )}
          {library?.drawMany && toDraw.length > 0 && (
            <div className="sa-draw-looks">
              {toDraw.map((id) => spec.items.find((o) => o.id === id)).filter(Boolean).map((o) => (
                <label key={o.id} className="sa-draw-look">
                  <span>{o.item}</span>
                  <input type="text" maxLength={160} aria-label={`How ${o.item} should look`} value={drawLooks[o.id] || ''} disabled={drawing}
                    placeholder={o.looks || 'How it should look (optional)'} onChange={(e) => setDrawLooks((l) => ({ ...l, [o.id]: e.target.value }))} />
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
